import { groupByDictUnique } from './utils';

type TLanguageDef = {
  i18nextCode: string; // the locale code as i18next is aware of it
  sitePathRoot: string; // this will be shown in the root of the path
  localeCode: string; // the full locale to be used in the hrefLang
  nativeName: string; // the name to show in the menu (native language name)
};

export const SITE_LANGUAGES: TLanguageDef[] = [
  {
    i18nextCode: 'de',
    sitePathRoot: 'de',
    localeCode: 'de-de',
    nativeName: 'Deutsch'
  },
  {
    i18nextCode: 'en',
    sitePathRoot: '',
    localeCode: 'en-us',
    nativeName: 'English'
  },
  {
    i18nextCode: 'es',
    sitePathRoot: 'es',
    localeCode: 'es-es',
    nativeName: 'Español'
  },
  {
    i18nextCode: 'fr',
    sitePathRoot: 'fr',
    localeCode: 'fr-fr',
    nativeName: 'Français'
  },
  {
    i18nextCode: 'it',
    sitePathRoot: 'it',
    localeCode: 'it-it',
    nativeName: 'Italiano'
  },
  {
    i18nextCode: 'pt',
    sitePathRoot: 'br',
    localeCode: 'pt-br',
    nativeName: 'Português Brasileiro'
  },
  {
    i18nextCode: 'uk',
    sitePathRoot: 'uk',
    localeCode: 'uk-ua',
    nativeName: 'Українська'
  },
  {
    i18nextCode: 'ru',
    sitePathRoot: 'ru',
    localeCode: 'ru-ru',
    nativeName: 'Русский'
  },
  {
    i18nextCode: 'ko',
    sitePathRoot: 'kr',
    localeCode: 'ko-kr',
    nativeName: '한국어'
  },
  {
    i18nextCode: 'zh',
    sitePathRoot: 'cn',
    localeCode: 'zh-cn',
    nativeName: '简体中文'
  }
];

export const SITE_LANGUAGES_BY_I18CODE = groupByDictUnique(SITE_LANGUAGES, 'i18nextCode');

// Languages eligible for auto-detection from Accept-Language header.
// Only Chinese for now; add more i18nextCodes to expand.
export const AUTO_DETECT_LANGUAGES = new Set(['zh']);

const AUTO_DETECT_LANGUAGE_DEFS = SITE_LANGUAGES.filter((l) => AUTO_DETECT_LANGUAGES.has(l.i18nextCode));

function normalizeLanguageCode(code: string): string {
  return code.trim().toLowerCase().split('-')[0];
}

/**
 * Parse an Accept-Language header and return the best matching language
 * definition from AUTO_DETECT_LANGUAGES, or null if no match.
 */
export function matchAutoDetectLanguage(acceptLanguageHeader: string): TLanguageDef | null {
  if (!acceptLanguageHeader) {
    return null;
  }

  const entries = acceptLanguageHeader
    .split(',')
    .map((entry) => {
      const parts = entry.trim().split(';');
      const tag = parts[0].trim();
      const quality = parts[1] ? parseFloat(parts[1].split('=')[1]) : 1;
      return { tag, quality };
    })
    .filter((entry) => entry.tag && Number.isFinite(entry.quality) && entry.quality > 0);

  entries.sort((a, b) => b.quality - a.quality);

  for (const { tag } of entries) {
    const primaryTag = normalizeLanguageCode(tag);
    const langDef = AUTO_DETECT_LANGUAGE_DEFS.find((l) => l.i18nextCode === primaryTag);
    if (langDef) {
      return langDef;
    }
  }

  return null;
}

// Path prefixes that are not browsable pages (static assets, API, etc.)
const NON_PAGE_PATH_PREFIXES = ['/api', '/images', '/uploads', '/assets'];

const LANGUAGE_COOKIE_NAME = 'rio-language';
const LANGUAGE_COOKIE_REGEX = new RegExp(`(?:^|;\\s*)${LANGUAGE_COOKIE_NAME}=([^;]*)`);

function getLanguageFromCookie(cookieHeader: string): TLanguageDef | null {
  if (!cookieHeader) {
    return null;
  }

  const match = cookieHeader.match(LANGUAGE_COOKIE_REGEX);
  if (!match) {
    return null;
  }

  let cookieLanguage = '';
  try {
    cookieLanguage = decodeURIComponent(match[1]).trim().toLowerCase();
  } catch (err) {
    cookieLanguage = match[1].trim().toLowerCase();
  }

  if (!cookieLanguage) {
    return null;
  }

  const normalizedCookieLanguage = normalizeLanguageCode(cookieLanguage);
  return (
    SITE_LANGUAGES.find(
      (lang) =>
        lang.i18nextCode === cookieLanguage ||
        lang.sitePathRoot === cookieLanguage ||
        lang.i18nextCode === normalizedCookieLanguage
    ) || null
  );
}

/**
 * Given request details, determine if we should auto-redirect to a localized
 * path based on the Accept-Language header. Returns the redirect URL string
 * if a redirect should happen, or null otherwise.
 *
 * @param reqPath - The request path (e.g. "/" or "/characters")
 * @param queryString - The query string including "?" (e.g. "?foo=bar"), or empty string
 * @param cookieHeader - The raw Cookie header value
 * @param acceptLanguageHeader - The raw Accept-Language header value
 */
export function getAutoDetectRedirectUrl(
  reqPath: string,
  queryString: string,
  cookieHeader: string,
  acceptLanguageHeader: string
): string | null {
  const normalizedPath = reqPath || '/';
  const pathParts = normalizedPath.split('/').filter((part) => part !== '');
  const existingPathLocale = pathParts[0] || '';

  const isPageRequest =
    !NON_PAGE_PATH_PREFIXES.some((prefix) => normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`)) &&
    normalizedPath !== '/favicon.ico';

  if (!isPageRequest) {
    return null;
  }

  if (existingPathLocale && isKnownLocalePrefix(existingPathLocale)) {
    return null;
  }

  // Honor explicit user preference when present and valid.
  const cookieLanguage = getLanguageFromCookie(cookieHeader);
  if (cookieLanguage) {
    if (!cookieLanguage.sitePathRoot) {
      // Default language is intentionally unlocalized.
      return null;
    }

    const redirectPath = setPathLocale(normalizedPath, cookieLanguage.i18nextCode);
    return redirectPath + queryString;
  }

  const langDef = matchAutoDetectLanguage(acceptLanguageHeader);
  if (!langDef?.sitePathRoot) {
    return null;
  }

  const redirectPath = setPathLocale(normalizedPath, langDef.i18nextCode);
  return redirectPath + queryString;
}

const DEFAULT_LOCALE_CODE = 'en-us';
const DEFAULT_SITE_LANGUAGE =
  SITE_LANGUAGES.find((entry) => entry.localeCode === DEFAULT_LOCALE_CODE) || SITE_LANGUAGES[0];

export const SUPPORTED_LANGUAGE_PATH_ROOTS = SITE_LANGUAGES.map((entry) => entry.sitePathRoot);

// Map i18nextCodes to sitePathRoot when they differ (handles /en/, /pt/, /ko/, /zh/ in URLs)
export const LOCALE_PATH_ALIASES: Record<string, string> = Object.fromEntries(
  SITE_LANGUAGES.filter((lang) => lang.i18nextCode !== lang.sitePathRoot).map((lang) => [lang.i18nextCode, lang.sitePathRoot])
);

// All recognized non-empty locale prefixes for URL matching (canonical + aliases)
export const ALL_RECOGNIZED_LOCALE_PREFIXES = [
  ...new Set([...SUPPORTED_LANGUAGE_PATH_ROOTS.filter((s) => s !== ''), ...Object.keys(LOCALE_PATH_ALIASES)])
];

// Regex matching any recognized locale prefix at the start of a URL path
// Note: all locale prefixes must be simple ASCII identifiers (no regex metacharacters)
export const LOCALE_PREFIX_REGEX = new RegExp(`^/+(${ALL_RECOGNIZED_LOCALE_PREFIXES.join('|')})($|/)`);

/** Check whether a URL path segment is a recognized locale prefix (canonical or alias). */
export function isKnownLocalePrefix(segment: string): boolean {
  return segment in LOCALE_PATH_ALIASES || SUPPORTED_LANGUAGE_PATH_ROOTS.includes(segment);
}

// add or override the locale in a given path
export function setPathLocale(path: string, code: string) {
  // the language entry we will use
  const lang = SITE_LANGUAGES.find((e) => e.i18nextCode === code || e.sitePathRoot === code) || DEFAULT_SITE_LANGUAGE;
  const isDefaultLanguage = lang.localeCode === DEFAULT_LOCALE_CODE;

  const pathParts = path.split(/\//g).filter((p) => p !== '');
  if (isKnownLocalePrefix(pathParts[0])) {
    // using a valid locale, so override this
    if (isDefaultLanguage) {
      // just remove the locale, since we are going to one where we don't show it
      pathParts.shift();
    } else {
      pathParts[0] = lang.sitePathRoot;
    }
  } else if (!isDefaultLanguage) {
    // need to prepend the language code
    pathParts.unshift(lang.sitePathRoot);
  }

  let results = '';
  if (pathParts.length === 1 && !isDefaultLanguage) {
    results = '/' + pathParts.join('/') + '/';
  } else {
    results = '/' + pathParts.join('/');
  }

  return results;
}

// add or override the locale in a given path
export function stripPathLocale(path: string) {
  const pathParts = path.split(/\//g).filter((p) => p !== '');
  if (isKnownLocalePrefix(pathParts[0])) {
    pathParts.shift();
  }
  return '/' + pathParts.join('/');
}
